import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, 
  RefreshCw, 
  Wrench, 
  Globe, 
  CheckCircle2, 
  Clock, 
  Mail, 
  Phone, 
  MapPin, 
  User, 
  ExternalLink, 
  Copy, 
  Check, 
  PenTool, 
  Share2, 
  FileText,
  Search,
  Filter,
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  Lock,
  Smartphone,
  LogIn,
  LogOut,
  AlertCircle,
  Trash2,
  Send,
  X,
  CheckCircle,
  Layers,
  Database,
  Server,
  Palette,
  CreditCard,
  Headphones
} from 'lucide-react';
import { EmailTemplateCustomizer } from './EmailTemplateCustomizer';
import { PagesCMSManager } from './PagesCMSManager';
import { 
  fetchServiceQuotes, 
  fetchWebsiteOnboardings, 
  updateServiceQuoteStatus, 
  updateWebsiteOnboardingStatus,
  deleteServiceQuote,
  deleteWebsiteOnboarding,
  ServiceQuoteRecord,
  WebsiteOnboardingRecord
} from '../lib/dbService';
import { generateAdminOtpEmail } from '../lib/emailTemplates';
import { auth, googleProvider } from '../lib/firebase';
import { signInWithPopup, signOut, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';

interface AdminDashboardProps {
  onBack: () => void;
}

// Authorized administrator check helper (only randon.brown.it@gmail.com and help@randonbrown.com)
const isAuthorizedAdmin = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const clean = email.trim().toLowerCase();
  return (
    clean === 'randon.brown.it@gmail.com' ||
    clean === 'help@randonbrown.com'
  );
};

// Resolve primary active inbox for an administrator
const resolveAdminEmail = (email: string | null | undefined): string => {
  if (!email) return 'randon.brown.it@gmail.com';
  return email.trim().toLowerCase();
};

// Dispatch OTP Email to authorized administrator
async function dispatchAdminOtpEmail(toEmail: string, otpCode: string): Promise<{ success: boolean; error?: string }> {
  try {
    const activeInbox = resolveAdminEmail(toEmail);
    const template = generateAdminOtpEmail(activeInbox, otpCode);

    const res = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: [activeInbox],
        replyTo: 'randon.brown.it@gmail.com',
        subject: template.subject,
        text: template.text,
        html: template.html,
        type: 'admin_alert'
      })
    });
    const result = (await res.json()) as { success?: boolean; error?: string; warning?: string };
    if (!res.ok || result.success === false) {
      return { success: false, error: result.error || result.warning || 'Email dispatch failed' };
    }
    return { success: true };
  } catch (err) {
    console.warn('Failed to dispatch OTP email:', err);
    return { success: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onBack }) => {
  const [activeSubTab, setActiveSubTab] = useState<'quotes' | 'websites' | 'templates' | 'integrations' | 'pages-cms'>('quotes');
  const [quotes, setQuotes] = useState<ServiceQuoteRecord[]>([]);
  const [onboardings, setOnboardings] = useState<WebsiteOnboardingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  // Auth & 2FA state
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isDomainError, setIsDomainError] = useState(false);
  
  // 2FA OTP States
  const [is2FAVerified, setIs2FAVerified] = useState<boolean>(() => {
    return sessionStorage.getItem('randon_admin_2fa_verified') === 'true';
  });
  const [generatedOtp, setGeneratedOtp] = useState<string>('');
  const [enteredOtp, setEnteredOtp] = useState<string>('');
  const [otpSentTime, setOtpSentTime] = useState<number>(0);
  const [otpCountdown, setOtpCountdown] = useState<number>(0);
  const [isSendingOtp, setIsSendingOtp] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [useBackupPin, setUseBackupPin] = useState<boolean>(false);
  const [backupPinInput, setBackupPinInput] = useState<string>('');
  const otpInputRef = useRef<HTMLInputElement>(null);

  const [recordToDelete, setRecordToDelete] = useState<{ id: string; type: 'quote' | 'website'; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const loadData = async () => {
    const adminEmail = auth.currentUser?.email || currentUser?.email;
    if (!adminEmail || !isAuthorizedAdmin(adminEmail) || !is2FAVerified) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      // 10s timeout protection so loading never hangs permanently
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Firestore request timed out. Please verify your connection or click Retry.')), 10000)
      );

      const [qData, oData] = await Promise.race([
        Promise.all([fetchServiceQuotes(), fetchWebsiteOnboardings()]),
        timeoutPromise
      ]);

      setQuotes(qData);
      setOnboardings(oData);
    } catch (err: unknown) {
      console.error('Error loading admin data from Firestore:', err);
      const msg = err instanceof Error ? err.message : 'Failed to query Firestore records';
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Generate and send fresh 6-digit OTP code
  const triggerSendOtp = async (userEmail: string) => {
    setIsSendingOtp(true);
    setOtpError(null);
    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(newOtp);
    setOtpSentTime(Date.now());
    setOtpCountdown(300); // 5 minutes

    const dispatchResult = await dispatchAdminOtpEmail(userEmail, newOtp);
    setIsSendingOtp(false);
    if (!dispatchResult.success) {
      setOtpError(
        `Email notice: Unable to deliver OTP via Resend (${dispatchResult.error || 'check Resend configuration'}). You may also sign in using your Emergency Master PIN (889932).`
      );
    }
  };

  // Countdown timer effect for OTP expiration
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const interval = setInterval(() => {
      setOtpCountdown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [otpCountdown]);

  // Monitor auth state and strictly allow only authorized administrators
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthLoading(false);
      if (user) {
        if (isAuthorizedAdmin(user.email)) {
          setCurrentUser(user);
          setAuthError(null);
          // If already 2FA verified for this session, load data directly
          if (sessionStorage.getItem('randon_admin_2fa_verified') === 'true') {
            setIs2FAVerified(true);
          } else {
            // Send OTP on initial login
            triggerSendOtp(resolveAdminEmail(user.email));
          }
        } else {
          // Immediately sign out unauthorized account
          await signOut(auth);
          setCurrentUser(null);
          setIs2FAVerified(false);
          sessionStorage.removeItem('randon_admin_2fa_verified');
          setQuotes([]);
          setOnboardings([]);
          setLoading(false);
          setAuthError(`Access Denied: The Google account "${user.email}" is not an authorized administrator. Unauthorized access attempt has been logged.`);
        }
      } else {
        setCurrentUser(null);
        setIs2FAVerified(false);
        sessionStorage.removeItem('randon_admin_2fa_verified');
        setQuotes([]);
        setOnboardings([]);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Reload data whenever 2FA verification succeeds OR when currentUser becomes available
  useEffect(() => {
    if (is2FAVerified && currentUser) {
      loadData();
    } else if (!authLoading && (!currentUser || !is2FAVerified)) {
      setLoading(false);
    }
  }, [is2FAVerified, currentUser, authLoading]);

  const handleGoogleSignIn = async () => {
    setAuthError(null);
    setIsDomainError(false);
    setIsSigningIn(true);
    try {
      // Force Google account chooser so the user can select their active account
      googleProvider.setCustomParameters({
        prompt: 'select_account'
      });
      await signOut(auth);

      const res = await signInWithPopup(auth, googleProvider);
      if (res.user) {
        if (isAuthorizedAdmin(res.user.email)) {
          setCurrentUser(res.user);
          setAuthError(null);
          // If not verified in current session, send OTP
          if (sessionStorage.getItem('randon_admin_2fa_verified') !== 'true') {
            triggerSendOtp(resolveAdminEmail(res.user.email));
          }
        } else {
          await signOut(auth);
          setCurrentUser(null);
          setIs2FAVerified(false);
          sessionStorage.removeItem('randon_admin_2fa_verified');
          setQuotes([]);
          setOnboardings([]);
          setAuthError(`Access Denied: The Google account "${res.user.email}" is not an authorized administrator. Unauthorized access attempt has been logged.`);
        }
      }
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Sign in failed';
      if (errMsg.includes('auth/popup-closed-by-user') || errMsg.includes('auth/cancelled-popup-request')) {
        // User closed or dismissed the popup window naturally - clean state without error trace
        console.info('Google sign-in popup was dismissed by the user.');
        setAuthError(null);
      } else if (errMsg.includes('auth/unauthorized-domain')) {
        console.warn('Firebase unauthorized domain:', errMsg);
        setIsDomainError(true);
        setAuthError(`This domain (${window.location.hostname}) is not yet in your Firebase Authorized Domains list.`);
      } else {
        console.error('Sign in error:', err);
        setAuthError(errMsg);
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleVerifyOtp = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setOtpError(null);

    const cleanInput = enteredOtp.trim();
    if (!cleanInput) {
      setOtpError('Please enter the 6-digit verification code sent to your email.');
      return;
    }

    if (otpCountdown <= 0 && generatedOtp) {
      setOtpError('This passcode has expired (5 min limit). Please click "Resend Code" to generate a new one.');
      return;
    }

    // Verify OTP matches or Master emergency passcode (8899)
    if (cleanInput === generatedOtp || cleanInput === '889932' || cleanInput === '573889') {
      setIs2FAVerified(true);
      sessionStorage.setItem('randon_admin_2fa_verified', 'true');
      setActionNotice('Two-Factor Authentication verified. Access granted.');
      setTimeout(() => setActionNotice(null), 4000);
      loadData();
    } else {
      setOtpError('Invalid verification code. Please check your email or request a new code.');
    }
  };

  const handleVerifyBackupPin = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setOtpError(null);
    const pin = backupPinInput.trim();
    if (pin === '889932' || pin === '573889' || pin === '2026') {
      setIs2FAVerified(true);
      sessionStorage.setItem('randon_admin_2fa_verified', 'true');
      setActionNotice('Emergency Master PIN verified. Access granted.');
      setTimeout(() => setActionNotice(null), 4000);
      loadData();
    } else {
      setOtpError('Incorrect Master Security PIN. Please try again or use email OTP.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setIs2FAVerified(false);
      sessionStorage.removeItem('randon_admin_2fa_verified');
      setQuotes([]);
      setOnboardings([]);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleStatusChangeQuote = async (id: string, newStatus: ServiceQuoteRecord['status']) => {
    const success = await updateServiceQuoteStatus(id, newStatus);
    if (success) {
      setQuotes(prev => prev.map(q => q.id === id ? { ...q, status: newStatus } : q));
    }
  };

  const handleStatusChangeOnboarding = async (id: string, newStatus: WebsiteOnboardingRecord['status']) => {
    const success = await updateWebsiteOnboardingStatus(id, newStatus);
    if (success) {
      setOnboardings(prev => prev.map(o => o.id === id ? { ...o, status: newStatus } : o));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;
    setIsDeleting(true);
    let success = false;
    if (recordToDelete.type === 'quote') {
      success = await deleteServiceQuote(recordToDelete.id);
      if (success) {
        setQuotes(prev => prev.filter(q => q.id !== recordToDelete.id));
        setActionNotice(`Quote from "${recordToDelete.name}" was permanently deleted.`);
      }
    } else {
      success = await deleteWebsiteOnboarding(recordToDelete.id);
      if (success) {
        setOnboardings(prev => prev.filter(o => o.id !== recordToDelete.id));
        setActionNotice(`Website onboarding for "${recordToDelete.name}" was permanently deleted.`);
      }
    }
    setIsDeleting(false);
    setRecordToDelete(null);
    if (!success) {
      setActionNotice('Failed to delete record. Please check your permissions or network.');
    }
    setTimeout(() => setActionNotice(null), 4000);
  };

  const handleEmailClientWithForwardQuote = (q: ServiceQuoteRecord) => {
    const toEmail = q.email?.trim() || 'help@randonbrown.com';
    const hasClientEmail = Boolean(q.email?.trim());
    const subject = encodeURIComponent(`Randon Brown Technical Services - Technical Service Quote (#${q.id.slice(0, 8).toUpperCase()})`);
    const body = encodeURIComponent(`Hi ${q.firstName},

Thank you for reaching out to Randon Brown Technical Services! I have reviewed your submission and wanted to follow up with you.

Best regards,
Randon Brown
help@randonbrown.com
(314) 944-3074
https://randonbrown.com

============================================================
TECHNICAL SERVICE QUOTE RECORD #${q.id.slice(0, 8).toUpperCase()}
============================================================
Date Submitted: ${q.createdAt}
Status: ${q.status}

CLIENT CONTACT:
- Name: ${q.firstName} ${q.lastName}
- Phone: ${q.phone}
- Email: ${q.email}
- Location: ${q.location}, ${q.zipCode}
- Address: ${q.address || 'N/A'} (${q.addressType})
- Service Mode: ${q.serviceMode}

TIMELINE & ISSUE:
- Urgency: ${q.urgency}
- When Needed: ${q.whenNeeded}
- Problem Description:
${q.problem}

AUTHORIZATION:
- Signed By: ${q.clientSignature}
- Date: ${q.signatureDate}`);

    const ccParam = hasClientEmail ? `&cc=help@randonbrown.com` : '';
    window.location.href = `mailto:${toEmail}?subject=${subject}${ccParam}&body=${body}`;
  };

  const handleEmailClientWithForwardOnboarding = (o: WebsiteOnboardingRecord) => {
    const toEmail = o.email?.trim() || 'help@randonbrown.com';
    const hasClientEmail = Boolean(o.email?.trim());
    const subject = encodeURIComponent(`Randon Brown Technical Services - Website Onboarding Intake (#${o.id.slice(0, 8).toUpperCase()})`);
    const body = encodeURIComponent(`Hi ${o.firstName},

Thank you for reaching out to Randon Brown Technical Services! I have reviewed your website project brief and wanted to follow up with you.

Best regards,
Randon Brown
help@randonbrown.com
(314) 944-3074
https://randonbrown.com

============================================================
WEBSITE ONBOARDING PROJECT BRIEF #${o.id.slice(0, 8).toUpperCase()}
============================================================
Date Submitted: ${o.createdAt}
Status: ${o.status}

BUSINESS & CONTACT:
- Business: ${o.businessName}
- Desired Domain: ${o.domainName}
- Contact: ${o.firstName} ${o.lastName}
- Phone: ${o.contactNumber}
- Email: ${o.email}
- Location: ${o.city}, ${o.zipCode}
- Tagline: ${o.tagline || 'N/A'}

PACKAGE & SCOPE:
- Package: ${o.selectedPackage}
- Layout Architecture: ${o.preferredLayout || 'classic_topbar'}${o.layoutNotes ? ` (Notes: ${o.layoutNotes})` : ''}
- Routing: ${o.leadOption}
- GitHub Account: ${o.githubUsername || 'None provided'}
- Core Pages: ${o.corePages}
- Brand Colors: ${o.brandColors || 'N/A'}
- Cloud Assets: ${o.cloudFolderLink || 'None'}
- Staged Files: ${o.stagedFileNames?.join(', ') || 'None'}

AUTHORIZATION:
- Signed By: ${o.clientSignature}
- Date: ${o.signatureDate}`);

    const ccParam = hasClientEmail ? `&cc=help@randonbrown.com` : '';
    window.location.href = `mailto:${toEmail}?subject=${subject}${ccParam}&body=${body}`;
  };

  const handleCopyRecord = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filtered lists
  const filteredQuotes = quotes.filter(q => {
    const matchesSearch = 
      `${q.firstName} ${q.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.phone.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.problem.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredOnboardings = onboardings.filter(o => {
    const matchesSearch = 
      `${o.firstName} ${o.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.domainName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-mono font-bold uppercase tracking-wider">New</span>;
      case 'contacted':
        return <span className="px-2.5 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20 text-[10px] font-mono font-bold uppercase tracking-wider">Contacted</span>;
      case 'in_progress':
        return <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-mono font-bold uppercase tracking-wider">In Progress</span>;
      case 'completed':
        return <span className="px-2.5 py-0.5 rounded-full bg-neutral-800 text-slate-300 border border-neutral-700 text-[10px] font-mono font-bold uppercase tracking-wider">Completed</span>;
      case 'archived':
        return <span className="px-2.5 py-0.5 rounded-full bg-neutral-900 text-slate-500 border border-neutral-800 text-[10px] font-mono font-bold uppercase tracking-wider">Archived</span>;
      default:
        return null;
    }
  };

  // Auth Loading State
  if (authLoading) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center space-y-4">
        <div className="p-6 rounded-2xl bg-neutral-950 border border-neutral-800 flex flex-col items-center justify-center space-y-3">
          <RefreshCw className="w-6 h-6 text-brand-400 animate-spin" />
          <p className="text-xs font-mono text-slate-400">Verifying administrator session...</p>
        </div>
      </div>
    );
  }

  // Unauthenticated Gate: Show Sign In button and hide the database inbox
  if (!currentUser) {
    return (
      <div className="space-y-8 max-w-2xl mx-auto px-4 sm:px-6 py-6 text-left">
        {/* Top Navigation */}
        <div className="flex items-center justify-between border-b border-neutral-900 pb-4">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-neutral-900/80 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 hover:border-brand-500/40 text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer group active:scale-95 w-fit"
          >
            <ArrowLeft className="w-4 h-4 text-brand-400 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Site</span>
          </button>

          <span className="text-[11px] font-mono text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-brand-400" />
            Owner Verification Required
          </span>
        </div>

        {/* Owner Sign In Card */}
        <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950 border border-neutral-800/90 shadow-2xl relative overflow-hidden space-y-6">
          <div className="absolute -top-24 -right-24 w-60 h-60 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-60 h-60 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="space-y-3 relative z-10 text-center sm:text-left">
            <div className="inline-flex items-center gap-2 py-1 px-3 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider font-mono">
              <ShieldCheck className="w-3.5 h-3.5" />
              Randon Brown, LLC Admin Portal
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-white">
              Administrator Sign In
            </h1>
            
            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
              Sign in with an authorized administrator account. Unauthorized attempts are logged and recorded.
            </p>
          </div>

          {authError && (
            <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs space-y-3 relative z-10">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="space-y-1 flex-1">
                  <span className="font-bold text-white text-sm block">
                    {isDomainError ? 'Domain Authorization Required' : 'Admin Account Authorization Required'}
                  </span>
                  <p className="text-amber-300/90 leading-relaxed">{authError}</p>
                </div>
              </div>

              {!isDomainError && (
                <div className="pt-2 border-t border-amber-500/20 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-300">
                    Authorized accounts: <strong className="text-white">randon.brown.it@gmail.com</strong> or <strong className="text-white">help@randonbrown.com</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setAuthError(null);
                      handleGoogleSignIn();
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 hover:text-white border border-amber-500/30 font-mono text-xs font-semibold cursor-pointer transition-all active:scale-95"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Choose Different Google Account</span>
                  </button>
                </div>
              )}

              {isDomainError && (
                <div className="pt-2 border-t border-amber-500/20 space-y-2 text-xs text-slate-300">
                  <p className="text-amber-200 font-semibold">How to authorize your custom domain in 30 seconds:</p>
                  <ol className="list-decimal list-inside space-y-1 text-slate-300">
                    <li>Click the button below to open Firebase Console.</li>
                    <li>Under <strong className="text-white">Authentication &gt; Settings &gt; Authorized domains</strong>, click <strong className="text-white">Add domain</strong>.</li>
                    <li>Add <code className="px-1.5 py-0.5 rounded bg-black/50 text-brand-300 font-mono">randonbrown.com</code> and <code className="px-1.5 py-0.5 rounded bg-black/50 text-brand-300 font-mono">www.randonbrown.com</code>.</li>
                    <li>Click <strong className="text-white">Save</strong> and click Sign In again.</li>
                  </ol>

                  <div className="pt-2">
                    <a
                      href="https://console.firebase.google.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 font-bold text-xs uppercase tracking-wider font-mono transition-all shadow-md active:scale-95"
                    >
                      <span>Open Firebase Console</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Primary Action Button */}
          <div className="pt-2 relative z-10">
            <button
              onClick={handleGoogleSignIn}
              disabled={isSigningIn}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-2xl bg-brand-500 hover:bg-brand-400 text-neutral-950 font-bold text-sm tracking-wide font-mono uppercase transition-all shadow-lg hover:shadow-brand-500/25 active:scale-[0.98] cursor-pointer disabled:opacity-50"
            >
              {isSigningIn ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Connecting to Google...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  <span>Sign In with Google</span>
                </>
              )}
            </button>
          </div>

          {/* Security & Features Checklist */}
          <div className="pt-6 border-t border-neutral-900 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-400 relative z-10">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Real-Time Cloud Firestore Sync</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Service Quote Diagnostics Inbox</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Website Onboarding Briefs</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Direct Client Status Management</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2 Gate: Two-Factor Authentication (OTP / Passcode)
  if (!is2FAVerified) {
    return (
      <div className="space-y-8 max-w-xl mx-auto px-4 sm:px-6 py-6 text-left">
        {/* Top Navigation */}
        <div className="flex items-center justify-between border-b border-neutral-900 pb-4">
          <button
            onClick={handleSignOut}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-neutral-900/80 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold transition-all cursor-pointer active:scale-95"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-brand-400" />
            <span>Cancel & Sign Out</span>
          </button>

          <span className="text-[11px] font-mono text-brand-400 flex items-center gap-1.5 font-semibold">
            <Lock className="w-3.5 h-3.5" />
            2FA Security Verification
          </span>
        </div>

        {/* 2FA Verification Card */}
        <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950 border border-brand-500/30 shadow-2xl relative overflow-hidden space-y-6">
          <div className="absolute -top-24 -right-24 w-60 h-60 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="space-y-3 relative z-10 text-center sm:text-left">
            <div className="inline-flex items-center gap-2 py-1 px-3 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider font-mono">
              <KeyRound className="w-3.5 h-3.5" />
              Two-Factor Authentication (OTP)
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-white">
              Enter Verification Code
            </h1>
            
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              We dispatched a secure 6-digit one-time passcode (OTP) to your authorized administrator email: <strong className="text-white font-mono">{resolveAdminEmail(currentUser.email)}</strong>.
            </p>
          </div>

          {otpError && (
            <div className="p-3.5 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center gap-2.5 relative z-10">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <span>{otpError}</span>
            </div>
          )}

          {!useBackupPin ? (
            /* OTP Form */
            <form onSubmit={handleVerifyOtp} className="space-y-5 relative z-10">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-mono">
                  <label className="text-slate-300 font-bold uppercase">6-Digit One-Time Code</label>
                  <span className={`font-semibold flex items-center gap-1 ${otpCountdown < 60 ? 'text-amber-400' : 'text-slate-400'}`}>
                    <Clock className="w-3.5 h-3.5" />
                    {Math.floor(otpCountdown / 60)}:{(otpCountdown % 60).toString().padStart(2, '0')}
                  </span>
                </div>
                
                <input
                  ref={otpInputRef}
                  type="text"
                  maxLength={6}
                  autoFocus
                  placeholder="000000"
                  value={enteredOtp}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setEnteredOtp(val);
                    if (val.length === 6) {
                      setTimeout(() => {
                        if (val === generatedOtp || val === '889932' || val === '573889') {
                          setIs2FAVerified(true);
                          sessionStorage.setItem('randon_admin_2fa_verified', 'true');
                        }
                      }, 100);
                    }
                  }}
                  className="w-full text-center text-3xl sm:text-4xl tracking-[0.4em] font-mono font-black py-4 px-4 rounded-2xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 text-brand-400 placeholder-slate-700 outline-none transition-all shadow-inner"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={enteredOtp.length !== 6}
                  className="w-full sm:flex-1 py-3.5 px-6 rounded-2xl bg-brand-500 hover:bg-brand-400 text-neutral-950 font-bold text-xs uppercase tracking-wider font-mono transition-all shadow-lg active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verify & Unlock Inbox
                </button>

                <button
                  type="button"
                  onClick={() => triggerSendOtp(currentUser?.email || 'randon.brown.it@gmail.com')}
                  disabled={isSendingOtp || otpCountdown > 240}
                  className="w-full sm:w-auto py-3.5 px-4 rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold transition-all cursor-pointer disabled:opacity-40"
                >
                  {isSendingOtp ? (
                    <span className="flex items-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending...
                    </span>
                  ) : (
                    <span>Resend Code</span>
                  )}
                </button>
              </div>

              <div className="pt-3 border-t border-neutral-900 flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => { setUseBackupPin(true); setOtpError(null); }}
                  className="text-slate-400 hover:text-brand-300 underline cursor-pointer text-[11px]"
                >
                  Use Master Admin PIN instead
                </button>

                <span className="text-[11px] text-slate-500 font-mono">
                  Sent to {currentUser.email}
                </span>
              </div>
            </form>
          ) : (
            /* Backup PIN Form */
            <form onSubmit={handleVerifyBackupPin} className="space-y-5 relative z-10">
              <div className="space-y-2">
                <label className="text-xs font-mono font-bold uppercase text-slate-300">Emergency Master PIN</label>
                <input
                  type="password"
                  autoFocus
                  placeholder="••••••"
                  value={backupPinInput}
                  onChange={e => setBackupPinInput(e.target.value)}
                  className="w-full text-center text-2xl tracking-widest font-mono py-3.5 px-4 rounded-2xl bg-neutral-900 border border-neutral-800 focus:border-brand-500 text-white placeholder-slate-700 outline-none transition-all"
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  className="flex-1 py-3.5 px-6 rounded-2xl bg-brand-500 hover:bg-brand-400 text-neutral-950 font-bold text-xs uppercase tracking-wider font-mono transition-all shadow-lg active:scale-95 cursor-pointer"
                >
                  Verify Master PIN
                </button>

                <button
                  type="button"
                  onClick={() => { setUseBackupPin(false); setOtpError(null); }}
                  className="py-3.5 px-4 rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 text-xs font-semibold transition-all cursor-pointer"
                >
                  Back to Email OTP
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Authenticated View: The Firebase Database Inbox
  return (
    <div className="space-y-8 max-w-6xl mx-auto px-3 sm:px-6 py-4 text-left">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-neutral-900 pb-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-neutral-900/80 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 hover:border-brand-500/40 text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer group active:scale-95 w-fit"
        >
          <ArrowLeft className="w-4 h-4 text-brand-400 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Site</span>
        </button>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Quick Zoho Links */}
          <div className="flex items-center gap-1.5 bg-neutral-900/60 p-1 rounded-xl border border-neutral-800/80">
            <a
              href="https://desk.zoho.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg hover:bg-neutral-800 text-slate-300 hover:text-brand-300 text-xs font-mono font-semibold transition-colors group"
              title="Open Zoho Desk Login (desk.zoho.com)"
            >
              <Headphones className="w-3.5 h-3.5 text-brand-400 group-hover:scale-110 transition-transform" />
              <span>Zoho Desk</span>
              <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
            </a>

            <span className="w-px h-3.5 bg-neutral-800" />

            <a
              href="https://mail.zoho.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg hover:bg-neutral-800 text-slate-300 hover:text-brand-300 text-xs font-mono font-semibold transition-colors group"
              title="Open Zoho Mail Login (mail.zoho.com)"
            >
              <Mail className="w-3.5 h-3.5 text-brand-400 group-hover:scale-110 transition-transform" />
              <span>Zoho Mail</span>
              <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
            </a>
          </div>

          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold cursor-pointer transition-all active:scale-95 disabled:opacity-50"
            title="Refresh database records"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-brand-400 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <div className="flex items-center gap-2.5 bg-neutral-900/80 border border-neutral-800 px-3 py-1.5 rounded-xl">
            <span className="text-xs text-slate-300 truncate max-w-[150px] font-mono">
              {currentUser.email}
            </span>
            <button
              onClick={handleSignOut}
              className="text-[11px] text-red-400 hover:text-red-300 flex items-center gap-1 cursor-pointer font-semibold ml-1"
              title="Sign Out"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {actionNotice && (
        <div className="p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center justify-between gap-2 shadow-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{actionNotice}</span>
          </div>
          <button 
            onClick={() => setActionNotice(null)} 
            className="text-slate-400 hover:text-white p-1"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {authError && (
        <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs space-y-3">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-bold text-white text-sm block">Domain Authorization Required for Google Login</span>
              <p className="text-amber-300/90 leading-relaxed">{authError}</p>
            </div>
          </div>

          {isDomainError && (
            <div className="pt-2 border-t border-amber-500/20 space-y-2 text-xs text-slate-300">
              <p className="text-amber-200 font-semibold">How to authorize your custom domain (takes 30 seconds):</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-300">
                <li>Click the button below to open your Firebase Console Authentication settings.</li>
                <li>Scroll down to the <strong className="text-white">Authorized domains</strong> section.</li>
                <li>Click <strong className="text-white">Add domain</strong> and type <code className="px-1.5 py-0.5 rounded bg-black/50 text-brand-300 font-mono">randonbrown.com</code> (and <code className="px-1.5 py-0.5 rounded bg-black/50 text-brand-300 font-mono">www.randonbrown.com</code>).</li>
                <li>Click <strong className="text-white">Save</strong>, then return here and click <strong className="text-white">Owner Sign In</strong>!</li>
              </ol>

              <div className="pt-2">
                <a
                  href="https://console.firebase.google.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-500 hover:bg-brand-400 text-neutral-950 font-bold text-xs uppercase tracking-wider font-mono transition-all shadow-md active:scale-95"
                >
                  <span>Open Firebase Console</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Title & Stats Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 py-1 px-3 rounded-full bg-brand-500/10 text-brand-400 text-xs font-bold uppercase tracking-wider mb-2 font-mono">
            <ShieldCheck className="w-3.5 h-3.5" />
            Firebase Database Inbox
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-white">
            Client Inquiries & Onboarding
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Real-time submissions from your Service Quote form and Website Onboarding guide.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex flex-wrap items-center gap-2 bg-neutral-900/90 p-1.5 rounded-2xl border border-neutral-800 w-fit">
          <button
            onClick={() => setActiveSubTab('quotes')}
            className={`px-4 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeSubTab === 'quotes'
                ? 'bg-brand-500 text-neutral-950 shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Wrench className="w-3.5 h-3.5" />
            <span>Quotes ({quotes.length})</span>
          </button>

          <button
            onClick={() => setActiveSubTab('websites')}
            className={`px-4 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeSubTab === 'websites'
                ? 'bg-brand-500 text-neutral-950 shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            <span>Websites ({onboardings.length})</span>
          </button>

          <button
            onClick={() => setActiveSubTab('templates')}
            className={`px-4 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeSubTab === 'templates'
                ? 'bg-brand-500 text-neutral-950 shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Palette className="w-3.5 h-3.5" />
            <span>Email Templates</span>
          </button>

          <button
            onClick={() => setActiveSubTab('integrations')}
            className={`px-4 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeSubTab === 'integrations'
                ? 'bg-brand-500 text-neutral-950 shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Integrations</span>
          </button>

          <button
            onClick={() => setActiveSubTab('pages-cms')}
            className={`px-4 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
              activeSubTab === 'pages-cms'
                ? 'bg-brand-500 text-neutral-950 shadow-md'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Pages CMS &amp; SEO</span>
          </button>

          {/* Direct Zoho Quotes Dashboard Link */}
          <a
            href="https://invoice.zoho.com"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3.5 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-1.5 bg-neutral-950/80 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 hover:border-brand-500/40 transition-all cursor-pointer shadow-sm group"
            title="Open Zoho Quote Dashboard (invoice.zoho.com) in a new tab"
          >
            <CreditCard className="w-3.5 h-3.5 text-brand-400 group-hover:scale-110 transition-transform" />
            <span>Open Zoho Quote Dashboard</span>
            <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-brand-400 transition-colors" />
          </a>
        </div>
      </div>

      {/* Zoho Workspace Operations Quick Access Bar */}
      <div className="p-3.5 rounded-2xl bg-neutral-950/80 border border-brand-500/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-[0_0_25px_rgba(245,158,11,0.04)]">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20 shrink-0">
            <Layers className="w-4 h-4" />
          </div>
          <div>
            <div className="text-xs font-bold text-white font-mono uppercase tracking-wider flex items-center gap-2">
              <span>Zoho Workspace Operations</span>
              <span className="text-[10px] text-brand-400 bg-brand-500/10 px-2 py-0.5 rounded-full border border-brand-500/20 font-mono">Direct Logins</span>
            </div>
            <p className="text-[11px] text-slate-400">1-click direct login portals for customer helpdesk, business webmail, and quotes</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Zoho Desk Login */}
          <a
            href="https://desk.zoho.com"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3.5 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-slate-200 hover:text-brand-300 border border-neutral-800 hover:border-brand-500/40 transition-all cursor-pointer shadow-sm group active:scale-95"
            title="Open Zoho Desk Login (desk.zoho.com) - Support Tickets & Client Helpdesk"
          >
            <Headphones className="w-3.5 h-3.5 text-brand-400 group-hover:scale-110 transition-transform" />
            <span>Zoho Desk</span>
            <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-brand-400 transition-colors" />
          </a>

          {/* Zoho Mail Login */}
          <a
            href="https://mail.zoho.com"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3.5 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-slate-200 hover:text-brand-300 border border-neutral-800 hover:border-brand-500/40 transition-all cursor-pointer shadow-sm group active:scale-95"
            title="Open Zoho Mail Login (mail.zoho.com) - Business Domain Email Workspace"
          >
            <Mail className="w-3.5 h-3.5 text-brand-400 group-hover:scale-110 transition-transform" />
            <span>Zoho Mail</span>
            <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-brand-400 transition-colors" />
          </a>

          {/* Zoho Quote Dashboard */}
          <a
            href="https://invoice.zoho.com"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3.5 py-2 rounded-xl text-xs font-bold font-mono uppercase tracking-wider flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-slate-200 hover:text-brand-300 border border-neutral-800 hover:border-brand-500/40 transition-all cursor-pointer shadow-sm group active:scale-95"
            title="Open Zoho Quote & Invoice Dashboard (invoice.zoho.com)"
          >
            <CreditCard className="w-3.5 h-3.5 text-brand-400 group-hover:scale-110 transition-transform" />
            <span>Zoho Quotes</span>
            <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-brand-400 transition-colors" />
          </a>
        </div>
      </div>

      {/* Search and Filters (Shown for quotes and websites) */}
      {(activeSubTab === 'quotes' || activeSubTab === 'websites') && (
        <div className="flex flex-col sm:flex-row items-center gap-3">
        <div className="relative w-full sm:flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by client name, email, phone, location, or notes..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-neutral-950 border border-neutral-800 focus:border-brand-500 focus:outline-none text-xs sm:text-sm text-white placeholder-slate-500 transition-colors"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 rounded-xl bg-neutral-950 border border-neutral-800 text-xs text-slate-300 focus:border-brand-500 focus:outline-none cursor-pointer w-full sm:w-auto"
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>
      )}

      {/* Database Error Banner if query failed */}
      {fetchError && (
        <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-xs text-red-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <span className="font-bold text-white block">Error Querying Firestore Database</span>
              <p className="text-red-300/90 leading-relaxed font-mono text-[11px]">{fetchError}</p>
            </div>
          </div>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold text-xs uppercase font-mono tracking-wider transition-all cursor-pointer shadow-md shrink-0 w-fit"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Retry Query</span>
          </button>
        </div>
      )}

      {/* List Container */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 space-y-4 rounded-2xl bg-neutral-950/60 border border-neutral-900">
          <RefreshCw className="w-8 h-8 text-brand-400 animate-spin mx-auto" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-white">Loading records from Firebase Firestore...</p>
            <p className="text-xs font-mono text-slate-500">Syncing service quotes & website onboarding briefs</p>
          </div>
          <div className="pt-1">
            <button
              onClick={loadData}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 text-xs font-mono text-slate-300 hover:text-white cursor-pointer transition-all"
            >
              <RefreshCw className="w-3 h-3 text-brand-400" />
              <span>Force Refresh</span>
            </button>
          </div>
        </div>
      ) : activeSubTab === 'quotes' ? (
        <div className="space-y-4">
          {filteredQuotes.length === 0 ? (
            <div className="p-12 rounded-2xl bg-neutral-950/60 border border-neutral-900 text-center text-slate-500 space-y-2">
              <Wrench className="w-8 h-8 mx-auto opacity-30" />
              <p className="text-sm font-semibold text-slate-400">No service quotes found.</p>
              <p className="text-xs">When customers submit quote requests, they will appear here instantly.</p>
            </div>
          ) : (
            filteredQuotes.map(q => {
              const summaryText = `SERVICE QUOTE REQUEST #${q.id.slice(0, 8).toUpperCase()}
Client: ${q.firstName} ${q.lastName}
Phone: ${q.phone}
Email: ${q.email}
Location: ${q.location}, ${q.zipCode}
Street: ${q.address || 'N/A'} (${q.addressType})
Service Mode: ${q.serviceMode}
Urgency: ${q.urgency} | Timeline: ${q.whenNeeded}
Problem: ${q.problem}
Signature: ${q.clientSignature} (${q.signatureDate})
Submitted: ${q.createdAt}`;

              return (
                <div
                  key={q.id}
                  className="p-5 sm:p-6 rounded-2xl bg-neutral-950 border border-neutral-800/90 hover:border-brand-500/30 transition-all space-y-4 shadow-lg"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-neutral-900 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400">
                        <Wrench className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-base">
                          {q.firstName} {q.lastName}
                        </h3>
                        <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400">
                          <span>Ref #{q.id.slice(0, 8).toUpperCase()}</span>
                          <span>•</span>
                          <span>{new Date(q.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {getStatusBadge(q.status)}
                      <select
                        value={q.status}
                        onChange={e => handleStatusChangeQuote(q.id, e.target.value as ServiceQuoteRecord['status'])}
                        className="px-2.5 py-1 rounded-lg bg-neutral-900 border border-neutral-800 text-[11px] text-slate-300 focus:border-brand-500 focus:outline-none cursor-pointer"
                      >
                        <option value="new">Mark New</option>
                        <option value="contacted">Mark Contacted</option>
                        <option value="in_progress">Mark In Progress</option>
                        <option value="completed">Mark Completed</option>
                        <option value="archived">Mark Archived</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-900 space-y-1">
                      <span className="text-slate-500 block text-[10px] font-mono uppercase">Contact Details</span>
                      {q.phone && (
                        <a href={`tel:${q.phone}`} className="text-white hover:text-brand-400 flex items-center gap-1.5 font-medium">
                          <Phone className="w-3 h-3 text-brand-400" />
                          <span>{q.phone}</span>
                        </a>
                      )}
                      {q.email && (
                        <a href={`mailto:${q.email}`} className="text-brand-400 hover:underline flex items-center gap-1.5 break-all">
                          <Mail className="w-3 h-3" />
                          <span>{q.email}</span>
                        </a>
                      )}
                    </div>

                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-900 space-y-1">
                      <span className="text-slate-500 block text-[10px] font-mono uppercase">Location & Service</span>
                      <span className="text-slate-300 block flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-brand-400" />
                        <span>{q.location} ({q.zipCode || 'No Zip'})</span>
                      </span>
                      {q.address && (
                        <span className="text-slate-400 text-[11px] block">{q.address} ({q.addressType})</span>
                      )}
                      <span className="text-white font-semibold block capitalize">Mode: {q.serviceMode}</span>
                    </div>

                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-900 space-y-1">
                      <span className="text-slate-500 block text-[10px] font-mono uppercase">Timeline & Urgency</span>
                      <span className="text-amber-400 font-semibold block flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{q.urgency}</span>
                      </span>
                      <span className="text-slate-400 text-[11px] block">Needed: {q.whenNeeded || 'ASAP'}</span>
                      {q.availability && (
                        <span className="text-slate-400 text-[11px] block">Availability: {q.availability}</span>
                      )}
                    </div>
                  </div>

                  {/* Problem Description */}
                  <div className="p-3.5 rounded-xl bg-neutral-900/40 border border-neutral-800 space-y-1 text-xs">
                    <span className="text-slate-500 block text-[10px] font-mono uppercase">Problem Description</span>
                    <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">{q.problem}</p>
                  </div>

                  {/* Signature Bar & Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-neutral-900 text-xs">
                    <div className="flex items-center gap-2">
                      <PenTool className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                      <span className="text-slate-400">Signed:</span>
                      <span className="text-brand-300 font-serif italic text-sm">{q.clientSignature}</span>
                      <span className="text-slate-500 font-mono text-[11px]">({q.signatureDate})</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <a
                        href="https://invoice.zoho.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 hover:border-brand-500/40 text-xs font-semibold cursor-pointer transition-all group"
                        title="Open Zoho Quote Dashboard (invoice.zoho.com)"
                      >
                        <CreditCard className="w-3 h-3 text-brand-400 group-hover:scale-105 transition-transform" />
                        <span>Open Zoho Quote Dashboard</span>
                        <ExternalLink className="w-2.5 h-2.5 text-slate-500 group-hover:text-brand-400 transition-colors" />
                      </a>

                      <button
                        onClick={() => handleEmailClientWithForwardQuote(q)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 hover:text-brand-300 border border-brand-500/30 text-xs font-semibold cursor-pointer transition-all"
                        title="Draft email to client with full forwarded quote details (sent from help@randonbrown.com)"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        <span>Email Client / Forward</span>
                      </button>

                      <button
                        onClick={() => handleCopyRecord(q.id, summaryText)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold cursor-pointer transition-all"
                      >
                        {copiedId === q.id ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-slate-400" />}
                        <span>{copiedId === q.id ? 'Copied' : 'Copy'}</span>
                      </button>

                      <button
                        onClick={() => setRecordToDelete({ id: q.id, type: 'quote', name: `${q.firstName} ${q.lastName}` })}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/30 text-xs font-semibold cursor-pointer transition-all"
                        title="Delete this quote from database"
                      >
                        <Trash2 className="w-3 h-3" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : activeSubTab === 'websites' ? (
        <div className="space-y-4">
          {filteredOnboardings.length === 0 ? (
            <div className="p-12 rounded-2xl bg-neutral-950/60 border border-neutral-900 text-center text-slate-500 space-y-2">
              <Globe className="w-8 h-8 mx-auto opacity-30" />
              <p className="text-sm font-semibold text-slate-400">No website onboarding submissions found.</p>
              <p className="text-xs">When clients complete the 5-step website guide, their project brief appears here.</p>
            </div>
          ) : (
            filteredOnboardings.map(o => {
              const logoSummaryText = o.needsLogo === 'need_logo'
                ? `Yes, Custom Logo Design Needed${o.logoNotes ? ` (Notes: ${o.logoNotes})` : ''}`
                : o.needsLogo === 'have_logo'
                ? 'Client has existing logo (will email / cloud)'
                : o.needsLogo === 'text_only'
                ? 'No logo needed / text wordmark'
                : 'Not specified';

              const summaryText = `WEBSITE ONBOARDING BRIEF #${o.id.slice(0, 8).toUpperCase()}
Client: ${o.firstName} ${o.lastName}
Contact: ${o.contactNumber} | ${o.email}
City: ${o.city}, ${o.zipCode}
Business Name: ${o.businessName}
Desired Domain: ${o.domainName}
Tagline: ${o.tagline}
Package: ${o.selectedPackage === 'package-a' ? 'Package A ($2,000)' : 'Package B ($4,000)'}
Layout Style: ${o.preferredLayout || 'classic_topbar'}${o.layoutNotes ? ` (Notes: ${o.layoutNotes})` : ''}
Logo Requirement: ${logoSummaryText}
Lead Routing: ${o.leadOption}
Pages: ${o.corePages}
Brand Colors: ${o.brandColors}
Cloud Assets Link: ${o.cloudFolderLink || 'None'}
Signature: ${o.clientSignature} (${o.signatureDate})
Submitted: ${o.createdAt}`;

              return (
                <div
                  key={o.id}
                  className="p-5 sm:p-6 rounded-2xl bg-neutral-950 border border-neutral-800/90 hover:border-brand-500/30 transition-all space-y-4 shadow-lg"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-neutral-900 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400">
                        <Globe className="w-4 h-4" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-base">
                          {o.businessName || `${o.firstName} ${o.lastName}`}
                        </h3>
                        <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400">
                          <span>Ref #{o.id.slice(0, 8).toUpperCase()}</span>
                          <span>•</span>
                          <span>Domain: <strong className="text-brand-400">{o.domainName || 'Unassigned'}</strong> {o.domainStatus === 'need_purchase' ? <span className="text-[10px] text-amber-400 font-normal">(Needs purchase)</span> : <span className="text-[10px] text-slate-400 font-normal">(Owns domain)</span>}</span>
                          <span>•</span>
                          <span>Hosting: <strong className="text-emerald-400">{o.hostingPreference === 'client_controlled' ? 'Client Controlled' : 'Randon Managed'}</strong></span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {getStatusBadge(o.status)}
                      <select
                        value={o.status}
                        onChange={e => handleStatusChangeOnboarding(o.id, e.target.value as WebsiteOnboardingRecord['status'])}
                        className="px-2.5 py-1 rounded-lg bg-neutral-900 border border-neutral-800 text-[11px] text-slate-300 focus:border-brand-500 focus:outline-none cursor-pointer"
                      >
                        <option value="new">Mark New</option>
                        <option value="contacted">Mark Contacted</option>
                        <option value="in_progress">Mark In Progress</option>
                        <option value="completed">Mark Completed</option>
                        <option value="archived">Mark Archived</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-900 space-y-1">
                      <span className="text-slate-500 block text-[10px] font-mono uppercase">Primary Contact</span>
                      <span className="text-white font-semibold block">{o.firstName} {o.lastName}</span>
                      {o.contactNumber && (
                        <a href={`tel:${o.contactNumber}`} className="text-slate-300 hover:text-brand-400 flex items-center gap-1.5 font-medium">
                          <Phone className="w-3 h-3 text-brand-400" />
                          <span>{o.contactNumber}</span>
                        </a>
                      )}
                      {o.email && (
                        <a href={`mailto:${o.email}`} className="text-brand-400 hover:underline flex items-center gap-1.5 break-all">
                          <Mail className="w-3 h-3" />
                          <span>{o.email}</span>
                        </a>
                      )}
                      {(o.city || o.zipCode) && (
                        <span className="text-slate-500 text-[11px] block">{o.city}{o.zipCode ? `, ${o.zipCode}` : ''}</span>
                      )}
                    </div>

                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-900 space-y-1">
                      <span className="text-slate-500 block text-[10px] font-mono uppercase">Architecture & Hosting</span>
                      <span className="font-bold text-white block">
                        {o.hostingPreference === 'client_controlled' ? 'Client Controlled Hosting' : 'Hosted by Randon (CDN Network)'}
                      </span>
                      <span className="text-amber-400 block text-[11px]">
                        Editing Style: {o.editingPreference === 'self_managed' ? 'Self-Managed (Edit Own Website)' : 'Complete Hands-Off ($75/hr Edits)'}
                      </span>
                      <span className="text-brand-400 block text-[11px]">
                        Routing: {o.leadOption === 'no-form' ? 'Direct Contact Only (No Form)' : 'Website Form (Direct Email Delivery)'}
                      </span>
                      {o.leadOption !== 'no-form' && (
                        <>
                          <span className="text-slate-300 block text-[11px]">
                            Fields: <span className="text-cyan-400">{(o.formFields && o.formFields.length > 0) ? o.formFields.join(', ') : 'Standard (Name, Email, Phone, Message)'}</span>
                            {o.customFormFields && <span className="text-slate-400 italic block mt-0.5">Note: {o.customFormFields}</span>}
                          </span>
                          {o.monthlySubmissions && (
                            <span className="text-emerald-400 block text-[11px]">
                              Volume: {o.monthlySubmissions}
                            </span>
                          )}
                        </>
                      )}
                      {o.githubUsername && (
                        <span className="text-slate-300 block text-[11px] font-mono">
                          GitHub: <span className="text-brand-400 font-semibold">@{o.githubUsername.replace(/^@/, '')}</span>
                        </span>
                      )}
                      {o.tagline && (
                        <span className="text-slate-400 text-[11px] italic block">"{o.tagline}"</span>
                      )}
                    </div>

                    <div className="p-3 rounded-xl bg-neutral-900/60 border border-neutral-900 space-y-1">
                      <span className="text-slate-500 block text-[10px] font-mono uppercase">Assets & Logo</span>
                      <div className="text-[11px] block">
                        <span className="text-slate-400">Logo: </span>
                        {o.needsLogo === 'need_logo' ? (
                          <span className="text-brand-400 font-bold">Needs Custom Logo {o.logoNotes ? `("${o.logoNotes}")` : ''}</span>
                        ) : o.needsLogo === 'text_only' ? (
                          <span className="text-slate-300">Wordmark Only</span>
                        ) : (
                          <span className="text-slate-300">Existing Logo</span>
                        )}
                      </div>
                      <span className="text-slate-300 block text-[11px]">Pages: {o.corePages || 'Home, About, Services, Contact'}</span>
                      {o.preferredLayout && (
                        <span className="text-brand-300 block text-[11px]">
                          Layout: <span className="font-semibold text-white capitalize">{o.preferredLayout.replace(/_/g, ' ')}</span>
                          {o.layoutNotes ? ` ("${o.layoutNotes}")` : ''}
                        </span>
                      )}
                      {o.brandColors && (
                        <span className="text-slate-400 text-[11px] block">Colors: {o.brandColors}</span>
                      )}
                    </div>
                  </div>

                  {/* Cloud Assets Link */}
                  {o.cloudFolderLink && (
                    <div className="p-3 rounded-xl bg-brand-500/5 border border-brand-500/20 text-xs flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 truncate">
                        <Share2 className="w-4 h-4 text-brand-400 shrink-0" />
                        <span className="text-slate-400 shrink-0">Shared Assets Link:</span>
                        <span className="text-brand-300 font-mono text-[11px] truncate">{o.cloudFolderLink}</span>
                      </div>
                      <a
                        href={o.cloudFolderLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2.5 py-1 rounded-lg bg-brand-500/20 hover:bg-brand-500 text-brand-400 hover:text-neutral-950 font-bold text-[11px] flex items-center gap-1 shrink-0 transition-colors"
                      >
                        <span>Open Drive</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}

                  {/* Signature Bar & Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-neutral-900 text-xs">
                    <div className="flex items-center gap-2">
                      <PenTool className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                      <span className="text-slate-400">Signed:</span>
                      <span className="text-brand-300 font-serif italic text-sm">{o.clientSignature}</span>
                      <span className="text-slate-500 font-mono text-[11px]">({o.signatureDate})</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <a
                        href="https://invoice.zoho.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 hover:border-brand-500/40 text-xs font-semibold cursor-pointer transition-all group"
                        title="Open Zoho Quote Dashboard (invoice.zoho.com)"
                      >
                        <CreditCard className="w-3 h-3 text-brand-400 group-hover:scale-105 transition-transform" />
                        <span>Open Zoho Quote Dashboard</span>
                        <ExternalLink className="w-2.5 h-2.5 text-slate-500 group-hover:text-brand-400 transition-colors" />
                      </a>

                      <button
                        onClick={() => handleEmailClientWithForwardOnboarding(o)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 hover:text-brand-300 border border-brand-500/30 text-xs font-semibold cursor-pointer transition-all"
                        title="Draft email to client with full forwarded onboarding brief (sent from help@randonbrown.com)"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        <span>Email Client / Forward</span>
                      </button>

                      <button
                        onClick={() => handleCopyRecord(o.id, summaryText)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold cursor-pointer transition-all"
                      >
                        {copiedId === o.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                        <span>{copiedId === o.id ? 'Copied' : 'Copy'}</span>
                      </button>

                      <button
                        onClick={() => setRecordToDelete({ id: o.id, type: 'website', name: o.businessName || `${o.firstName} ${o.lastName}` })}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/30 text-xs font-semibold cursor-pointer transition-all"
                        title="Delete this onboarding from database"
                      >
                        <Trash2 className="w-3 h-3" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : activeSubTab === 'templates' ? (
        <EmailTemplateCustomizer adminEmail={currentUser?.email || 'randon.brown.it@gmail.com'} />
      ) : activeSubTab === 'integrations' ? (
        /* Integrations Overview Subtab */
        <div className="space-y-4">
          <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-900 space-y-2">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-brand-400" />
              <span>Production Integrations & System Architecture</span>
            </h2>
            <p className="text-xs text-slate-400">
              Live status and configuration of all backend services, databases, email delivery routes, and payment gateways connected to randonbrown.com.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Resend */}
            <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-800/90 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Resend Email API</h3>
                    <span className="text-[10px] font-mono text-slate-500">Transactional Delivery</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  Domain Verified
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Dispatches instant intake alerts to <code className="text-brand-300">randon.brown.it@gmail.com</code> and sends branded confirmation receipts directly to clients from <code className="text-brand-300">help@randonbrown.com</code>.
              </p>
              <div className="pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 space-y-1">
                <div>• Verified Domain: randonbrown.com</div>
                <div>• Server Route: /api/send-email (Proxy Protected)</div>
                <div>• Direct Client Reply-To Configured</div>
              </div>
              <div className="pt-3 border-t border-neutral-900/80 flex flex-wrap items-center gap-2">
                <a
                  href="https://resend.com/emails"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-brand-400 hover:text-brand-300 border border-neutral-800 hover:border-brand-500/40 text-xs font-semibold cursor-pointer transition-all group"
                  title="Open Resend Email Delivery Dashboard"
                >
                  <Mail className="w-3 h-3 group-hover:scale-105 transition-transform" />
                  <span>Open Resend Console</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500 group-hover:text-brand-400 transition-colors" />
                </a>
                <a
                  href="https://resend.com/domains"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-neutral-950 hover:bg-neutral-900 text-slate-400 hover:text-white border border-neutral-800/80 text-xs transition-colors"
                  title="View Resend Domain Verification & DNS"
                >
                  <span>Domains & DNS</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
                </a>
              </div>
            </div>

            {/* Firestore */}
            <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-800/90 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                    <Database className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Google Cloud Firestore</h3>
                    <span className="text-[10px] font-mono text-slate-500">NoSQL Real-Time Database</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  Rule Enforced
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Encrypted cloud persistence for Service Quotes, Website Onboarding intake sheets, client digital signatures, and workflow statuses.
              </p>
              <div className="pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 space-y-1">
                <div>• Project: gen-lang-client-0815434209</div>
                <div>• Database: ai-studio-randonbrownitlea</div>
                <div>• Collections: service_quotes, website_onboardings, mail</div>
              </div>
              <div className="pt-3 border-t border-neutral-900/80 flex flex-wrap items-center gap-2">
                <a
                  href="https://console.firebase.google.com/project/gen-lang-client-0815434209/firestore"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-brand-400 hover:text-brand-300 border border-neutral-800 hover:border-brand-500/40 text-xs font-semibold cursor-pointer transition-all group"
                  title="Open Firebase Firestore Database Console"
                >
                  <Database className="w-3 h-3 group-hover:scale-105 transition-transform" />
                  <span>Open Firestore Console</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500 group-hover:text-brand-400 transition-colors" />
                </a>
                <a
                  href="https://console.firebase.google.com/project/gen-lang-client-0815434209/firestore/rules"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-neutral-950 hover:bg-neutral-900 text-slate-400 hover:text-white border border-neutral-800/80 text-xs transition-colors"
                  title="View Firestore Security Rules"
                >
                  <span>Security Rules</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
                </a>
              </div>
            </div>

            {/* Firebase Auth */}
            <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-800/90 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                    <ShieldCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Firebase Authentication</h3>
                    <span className="text-[10px] font-mono text-slate-500">Google OAuth 2.0 Security</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  Active SSO
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Cryptographic authentication protecting customer personal contact details, diagnostic data, and administrative management capabilities.
              </p>
              <div className="pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 space-y-1">
                <div>• Role Account: randon.brown.it@gmail.com / help@randonbrown.com</div>
                <div>• Provider: Google Identity SSO</div>
                <div>• 2FA OTP Protocol: SHA-256 session verification</div>
              </div>
              <div className="pt-3 border-t border-neutral-900/80 flex flex-wrap items-center gap-2">
                <a
                  href="https://console.firebase.google.com/project/gen-lang-client-0815434209/authentication/users"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-brand-400 hover:text-brand-300 border border-neutral-800 hover:border-brand-500/40 text-xs font-semibold cursor-pointer transition-all group"
                  title="Open Firebase Authentication Users Console"
                >
                  <ShieldCheck className="w-3 h-3 group-hover:scale-105 transition-transform" />
                  <span>Open Auth Users Console</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500 group-hover:text-brand-400 transition-colors" />
                </a>
                <a
                  href="https://console.firebase.google.com/project/gen-lang-client-0815434209/authentication/providers"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-neutral-950 hover:bg-neutral-900 text-slate-400 hover:text-white border border-neutral-800/80 text-xs transition-colors"
                  title="View Google Identity SSO & Sign-in Providers"
                >
                  <span>Sign-in Providers</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
                </a>
              </div>
            </div>

            {/* Express Server */}
            <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-800/90 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                    <Server className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Express Backend Proxy</h3>
                    <span className="text-[10px] font-mono text-slate-500">Full-Stack Security</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  Port 3000
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Server-side process isolating API keys and secrets so sensitive tokens are never exposed in browser bundles or downloaded by visitors.
              </p>
              <div className="pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 space-y-1">
                <div>• Proxy Endpoints: /api/send-email, /api/health</div>
                <div>• Type-safe payload validation</div>
              </div>
              <div className="pt-3 border-t border-neutral-900/80 flex flex-wrap items-center gap-2">
                <a
                  href="/api/health"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-brand-400 hover:text-brand-300 border border-neutral-800 hover:border-brand-500/40 text-xs font-semibold cursor-pointer transition-all group"
                  title="Verify Express Server Health endpoint (/api/health)"
                >
                  <Server className="w-3 h-3 group-hover:scale-105 transition-transform" />
                  <span>Check /api/health Status</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500 group-hover:text-brand-400 transition-colors" />
                </a>
                <a
                  href="https://console.cloud.google.com/run?project=gen-lang-client-0815434209"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-neutral-950 hover:bg-neutral-900 text-slate-400 hover:text-white border border-neutral-800/80 text-xs transition-colors"
                  title="Open Google Cloud Run / Server Console"
                >
                  <span>Cloud Console</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
                </a>
              </div>
            </div>

            {/* Google Cloud Platform & OAuth Credentials */}
            <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-800/90 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                    <Lock className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Google Cloud & OAuth 2.0</h3>
                    <span className="text-[10px] font-mono text-slate-500">API Credentials & Consent</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  Client ID Active
                </span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                Centralized Google Cloud project managing OAuth 2.0 Client credentials, authorized JavaScript origins, and API quotas.
              </p>
              <div className="pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 space-y-1">
                <div>• Project: gen-lang-client-0815434209</div>
                <div>• Client ID: 609509750761-rd5naik080ht2p3eb4rsunp2teumirqk...</div>
              </div>
              <div className="pt-3 border-t border-neutral-900/80 flex flex-wrap items-center gap-2">
                <a
                  href="https://console.cloud.google.com/apis/credentials?project=gen-lang-client-0815434209"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-brand-400 hover:text-brand-300 border border-neutral-800 hover:border-brand-500/40 text-xs font-semibold cursor-pointer transition-all group"
                  title="Open Google Cloud Credentials Console"
                >
                  <Lock className="w-3 h-3 group-hover:scale-105 transition-transform" />
                  <span>Open Credentials Console</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500 group-hover:text-brand-400 transition-colors" />
                </a>
                <a
                  href="https://console.cloud.google.com/apis/dashboard?project=gen-lang-client-0815434209"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-neutral-950 hover:bg-neutral-900 text-slate-400 hover:text-white border border-neutral-800/80 text-xs transition-colors"
                  title="View GCP API Metrics & Quotas"
                >
                  <span>API Dashboard</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
                </a>
              </div>
            </div>

            {/* Zoho Billing Portal & Quotes Dashboard */}
            <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-800/90 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                    <CreditCard className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Zoho Secure Invoicing & Quotes Dashboard</h3>
                    <span className="text-[10px] font-mono text-slate-500">Billing, Estimates & Payment Gateway</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20 w-fit">
                  Linked Gateway
                </span>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Dedicated external accounting system for creating itemized quote estimates, tracking client approvals, issuing official invoices, and accepting secure credit card payments.
              </p>

              <div className="pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 space-y-1">
                <div>• Quotes Dashboard: invoice.zoho.com</div>
                <div>• Client View: invoice.zohosecure.com/portal/iamrandonbrown</div>
                <div>• Capabilities: Estimate Approvals, Work Orders, Invoice PDF History, Online Payments</div>
              </div>

              <div className="pt-3 border-t border-neutral-900/80 flex flex-wrap items-center gap-2">
                <a
                  href="https://invoice.zoho.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider transition-all cursor-pointer shadow-md"
                  title="Open Zoho Quote Dashboard (invoice.zoho.com)"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  <span>Open Zoho Quote Dashboard</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="https://invoice.zohosecure.com/portal/iamrandonbrown"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs transition-colors"
                  title="View Client Billing Portal"
                >
                  <span>View Client Portal</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
                </a>
              </div>
            </div>

            {/* Zoho Desk Support Portal & Ticketing */}
            <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-800/90 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                    <Headphones className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Zoho Desk Customer Support Center</h3>
                    <span className="text-[10px] font-mono text-slate-500">Ticketing, Client Inquiries &amp; Helpdesk SLA</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20 w-fit">
                  Support Portal
                </span>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Centralized ticketing software for managing customer inquiries, support requests, incident escalations, automated responses, and service-level agreements.
              </p>

              <div className="pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 space-y-1">
                <div>• Login URL: desk.zoho.com</div>
                <div>• Capabilities: Multi-channel Ticket Management, Knowledge Base, Customer Feedback</div>
              </div>

              <div className="pt-3 border-t border-neutral-900/80 flex flex-wrap items-center gap-2">
                <a
                  href="https://desk.zoho.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider transition-all cursor-pointer shadow-md"
                  title="Open Zoho Desk Login (desk.zoho.com)"
                >
                  <Headphones className="w-3.5 h-3.5" />
                  <span>Open Zoho Desk Login</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Zoho Mail Business Workspace */}
            <div className="p-5 rounded-2xl bg-neutral-950 border border-neutral-800/90 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">Zoho Mail Business Workspace</h3>
                    <span className="text-[10px] font-mono text-slate-500">Secure Domain Webmail &amp; Admin Suite</span>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20 w-fit">
                  Domain Email
                </span>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Ad-free business email service hosting custom domain email addresses, mailbox routing, calendar synchronization, and contacts management.
              </p>

              <div className="pt-2 border-t border-neutral-900 text-[11px] font-mono text-slate-400 space-y-1">
                <div>• Webmail Login: mail.zoho.com</div>
                <div>• Mail Admin Console: mailadmin.zoho.com</div>
                <div>• Primary Addresses: help@randonbrown.com</div>
              </div>

              <div className="pt-3 border-t border-neutral-900/80 flex flex-wrap items-center gap-2">
                <a
                  href="https://mail.zoho.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-400 text-neutral-950 text-xs font-bold font-mono uppercase tracking-wider transition-all cursor-pointer shadow-md"
                  title="Open Zoho Mail Login (mail.zoho.com)"
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span>Open Zoho Mail Login</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="https://mailadmin.zoho.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs transition-colors"
                  title="Open Zoho Mail Admin Console (mailadmin.zoho.com)"
                >
                  <span>Mail Admin Console</span>
                  <ExternalLink className="w-2.5 h-2.5 text-slate-500" />
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : activeSubTab === 'pages-cms' ? (
        <PagesCMSManager />
      ) : null}

      {/* Delete Confirmation Modal */}
      {recordToDelete && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => !isDeleting && setRecordToDelete(null)}
          />
          <div className="relative w-full max-w-md bg-[#0e0e0e] border border-red-500/30 rounded-2xl p-6 shadow-2xl z-10 text-left space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-red-500/10 text-red-400 border border-red-500/20">
                <Trash2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Delete Record?</h3>
                <p className="text-xs text-slate-400">
                  {recordToDelete.type === 'quote' ? 'Service Quote' : 'Website Onboarding'}
                </p>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Are you sure you want to permanently delete the submission for <strong className="text-white font-semibold">"{recordToDelete.name}"</strong>? This action cannot be undone.
            </p>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-neutral-900">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setRecordToDelete(null)}
                className="px-4 py-2 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 text-xs font-bold transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleDeleteConfirm}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                <span>{isDeleting ? 'Deleting...' : 'Yes, Delete Permanently'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
